import React, { useState } from 'react';
import QrScanner from 'react-qr-scanner';
import { Button, Modal, Alert } from 'antd';
import { CameraOutlined, CloseOutlined, SwapOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import MainBtn from '../main/main_btn/MainBtn';

interface QRScannerProps {
  open: boolean;
  onClose: () => void;
  onScan: (data: { device_id: string; secret: string }) => void;
}

const QRScannerComponent: React.FC<QRScannerProps> = ({ open, onClose, onScan }) => {
  const { t } = useTranslation("home");
  const [error, setError] = useState<string | null>(null);
  const [scanning, setScanning] = useState(false);
  const [cameraFacing, setCameraFacing] = useState<'user' | 'environment'>('environment');

  const handleScan = (data: any) => {
    if (data) {
      // Extract the actual text from the scanned result
      const qrText = data.text || data;
      console.log('Scanned QR code text:', qrText);
      
      try {
        // Parse QR code data - expecting format like "device_id:secret" or JSON
        let deviceId = '';
        let secret = '';

        // Try to parse as JSON first
        try {
          const parsed = JSON.parse(qrText);
          deviceId = parsed.device_id || parsed.deviceId || '';
          secret = parsed.secret || '';
        } catch {
          // If not JSON, try colon-separated format
          const parts = qrText.split(':');
          if (parts.length === 2) {
            deviceId = parts[0].trim();
            secret = parts[1].trim();
          } else {
            throw new Error('Invalid QR code format');
          }
        }

        if (deviceId && secret) {
          onScan({ device_id: deviceId, secret });
          onClose();
        } else {
          setError(t('qr_scanner_invalid_format'));
        }
      } catch (err) {
        setError(t('qr_scanner_invalid_format'));
      }
    }
  };

  const handleError = (error: any) => {
    console.error('QR Scanner error:', error);
    setError(t('qr_scanner_camera_error'));
  };

  const startScanning = () => {
    setScanning(true);
    setError(null);
  };

  const stopScanning = () => {
    setScanning(false);
  };

  const switchCamera = () => {
    setCameraFacing(prev => prev === 'user' ? 'environment' : 'user');
  };

  const getCameraConstraints = () => {
    const baseConstraints: any = {
      facingMode: cameraFacing,
      width: { ideal: 1280, min: 640 },
      height: { ideal: 720, min: 480 },
      focusMode: 'continuous',
    };

    const advancedConstraints: any[] = [
      { focusMode: 'continuous' },
      { focusDistance: { min: 0.1, max: 1.0 } }
    ];

    return {
      video: {
        ...baseConstraints,
        advanced: advancedConstraints
      }
    };
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={t('qr_scanner_title')}
      footer={null}
      width={400}
      centered
      zIndex={9999}
      getContainer={() => document.body}
    >
      <div style={{ textAlign: 'center', padding: '20px 0' }}>
        {error && (
          <Alert
            message={error}
            type="error"
            style={{ marginBottom: 16 }}
            showIcon
          />
        )}
        
        {!scanning ? (
          <div>
            <CameraOutlined style={{ fontSize: '48px', color: '#1890ff', marginBottom: 16 }} />
            <p style={{ marginBottom: 16 }}>{t('qr_scanner_description')}</p>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <MainBtn
                title={t('qr_scanner_start_scanning')}
                icon={<CameraOutlined />}
                onClick={startScanning}
                color="primary"
              />
            </div>
          </div>
        ) : (
          <div>
            <div style={{ position: 'relative', marginBottom: 16 }}>
              <QrScanner
                delay={500}
                onError={handleError}
                onScan={handleScan}
                style={{ width: '100%' }}
                constraints={getCameraConstraints()}
              />
              <div style={{ position: 'absolute', top: 8, right: 8, display: 'flex', gap: '8px' }}>
                <MainBtn
                  title={t('qr_scanner_switch_camera')}
                  icon={<SwapOutlined />}
                  onClick={switchCamera}
                  color="secondary"
                />
                <MainBtn
                  title={t('qr_scanner_stop_scanning')}
                  icon={<CloseOutlined />}
                  onClick={stopScanning}
                  color="secondary"
                />
              </div>
            </div>
            <p style={{ fontSize: '12px', color: '#666' }}>
              {t('qr_scanner_instructions')}
            </p>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default QRScannerComponent;
